import React, { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Stack } from "@mui/system";
import { FormHelperText, IconButton, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormContext, useController, Controller } from "react-hook-form";
import uploadImg from "../../assets/cloud-upload.png";
import { ImageConfig } from "./FileConfig";

const CustomBox = styled(Box)({
  "&.MuiBox-root": {
    backgroundColor: "#fff",
    borderRadius: "2rem",
    boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    padding: "1rem",
  },
  "&.MuiBox-root:hover, &.MuiBox-root.dragover": {
    opacity: 0.6,
  },
});
const FileUpload = ({ required, limit, multiple, name }) => {
  const {
    control,
    formState: { isSubmitting, errors },
  } = useFormContext();
  const { field } = useController({ name, control });
  const [singleFile, setSingleFile] = useState([]);
  const [fileList, setFileList] = useState([]);
  const wrapperRef = useRef(null);

  //Toggle the dragover class
  const OnDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const OnDragLeave = () => wrapperRef.current?.classList.remove("dragover");

  const onFileDrop = useCallback(
    (e) => {
      const target = e.target;
      if (!target.files) {
        return;
      }
      if (limit === 1) {
        const newFile = Object.values(target.files).map((file) => file);
        if (singleFile.length >= 1) {
          return alert("Only a signle image allowed");
        }
        setSingleFile(newFile);
        field.onChange(newFile[0]);
      }
      if (multiple) {
        const newFiles = Object.values(target.files).map((file) => file);
        if (newFiles) {
          const updatedList = [...fileList, ...newFiles];
          if (updatedList.length > limit || newFiles.length > 3) {
            return alert(`Image must not be more than ${limit}`);
          }
          setFileList(updatedList);
          field.onChange(updatedList);
        }
      }
    },
    [field, fileList, limit, multiple, singleFile]
  );

  const fileRemove = (file) => {
    const updatedList = [...fileList];
    updatedList.splice(fileList.indexOf(file), 1);
    setFileList(updatedList);
  };
  const fileSingleRemove = () => {
    setSingleFile([]);
  };

  const calcSize = (size) => {
    return size < 1000000
      ? `${Math.floor(size / 1000)} KB`
      : `${Math.floor(size / 1000000)} MB`;
  };
  // useEffect(() => {
  //   if (isSubmitting) {
  //     setFileList([]);
  //     setSingleFile([]);
  //   }
  // }, [isSubmitting]);

  return (
    <>
      <CustomBox>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: "relative",
            width: "100%",
            height: "13rem",
            border: "2px dashed #4267b2",
            borderRadius: "20px",
          }}
          ref={wrapperRef}
          onDragEnter={OnDragEnter}
          onDragLeave={OnDragLeave}
          onDrop={OnDragLeave}
        >
          <Stack
            justifyContent={"center"}
            sx={{
              p: 1,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "#ccc" }}>
              {limit > 1 ? "Browse files to upload" : "Browse file to upload"}
            </Typography>
            <div>
              <img
                src={uploadImg}
                alt="file upload"
                style={{ width: "5rem" }}
              />
            </div>
            <Typography variant="body1" component="span">
              <strong>Supported Files</strong>
            </Typography>
            <Typography variant="body2" component="span">
              JPG, JPEG, PNG
            </Typography>
          </Stack>{" "}
          <Controller
            name={name}
            defaultValue=""
            control={control}
            rules={{
              required: required,
              // validate: {
              //   // lessThanTwo: (file) =>
              //   //   file?.size > 3048 || "The image must not be greater than 3mb",
              //   acceptedFormats: (files) =>
              //     ["image/jpeg", "image/jpg", "image/png"].includes(
              //       files?.type
              //     ) || "Only JPEG and PNG allowed",
              //   // message: 'The image must not be greater than 2mb',
              // },
            }}
            render={({ field: { name, onBlur, ref } }) => (
              <input
                type="file"
                name={name}
                onBlur={onBlur}
                ref={ref}
                onChange={onFileDrop}
                multiple={multiple}
                accept="image/jpg, image/png, image/jpeg"
                style={{
                  opacity: 0,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
              />
            )}
          />
        </Box>
      </CustomBox>{" "}
      <FormHelperText
        sx={{ textAlign: "center", my: 1 }}
        error={!!errors[name]}
      >
        {errors[name] ? errors[name]?.message : ""}
      </FormHelperText>
      {/* Image preview */}
      {fileList.length > 0 || singleFile.length > 0 ? (
        <Stack spacing={2} sx={{ my: 2 }}>
          {(multiple ? fileList : singleFile).map((item, index) => {
            const imageType = item.type.split("/")[1];
            return (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  backgroundColor: "#f5f8ff",
                  borderRadius: 1.5,
                  p: 0.5,
                }}
              >
                <Box display={"flex"}>
                  <img
                    src={ImageConfig[`${imageType}`] || ImageConfig["default"]}
                    alt="upload"
                    style={{
                      height: "3.5rem",
                      objectFit: "contain",
                    }}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography>{item.name}</Typography>
                    <Typography variant="body2">
                      {calcSize(item.size)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => {
                    if (multiple) {
                      fileRemove(item);
                    } else {
                      fileSingleRemove();
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </>
  );
};

export default FileUpload;
